/**
 * Step 2 for a platform that can discover: project (only those the connection
 * can see), then agent, then version, as three columns side by side so every
 * choice stays in view. Each version shows its number and a "production"
 * marker, nothing more; production is the default.
 */

import { ColumnList } from "@/components/ui/column-list";
import { Pill } from "@/components/ui/pill";
import { useGroups, useTargets, useVersions } from "../hooks";
import type { TargetPickerProps } from "../screens";
import { PRODUCTION } from "../types";

function VersionColumn({
  connectionId,
  value,
  onChange,
}: { connectionId: string } & Pick<TargetPickerProps, "value" | "onChange">) {
  const ready = !!value.group && !!value.target;
  const versions = useVersions(connectionId, value.group, value.target);
  const production = versions.data?.find((v) => v.is_production)?.version;
  const picked = value.version === PRODUCTION ? (production ?? null) : value.version;
  return (
    <ColumnList
      title="Version"
      name="target-version"
      items={
        ready
          ? versions.data?.map((v) => ({
              value: v.version,
              label: `v${v.version}`,
              mono: true,
              extra: v.is_production ? <Pill tone="run">production</Pill> : undefined,
            }))
          : undefined
      }
      value={picked ?? null}
      onChange={(v) => onChange({ version: v === production ? PRODUCTION : v })}
      waiting={!ready ? "Pick an agent first" : versions.isError ? "Could not list versions" : "Loading…"}
    />
  );
}

export function DiscoverTargetPicker({ connection, value, onChange, locked }: TargetPickerProps) {
  const groups = useGroups(connection.id);
  const targets = useTargets(connection.id, value.group);
  return (
    <div className="grid min-w-0 divide-line-soft max-md:divide-y md:grid-cols-3 md:divide-x">
      <ColumnList
        title="Project"
        name="target-group"
        disabled={locked}
        items={groups.data?.map((g) => ({ value: g.id, label: g.name }))}
        value={value.group ?? null}
        onChange={(g) => onChange({ group: g, target: null, version: PRODUCTION, agentName: "" })}
        waiting={groups.isError ? "Could not list projects through this connection." : "Loading…"}
      />
      <ColumnList
        title="Agent"
        name="target-name"
        disabled={locked}
        items={
          value.group
            ? targets.data?.map((t) => ({
                value: t.name,
                label: t.label || t.name,
                note: t.description || undefined,
              }))
            : undefined
        }
        value={value.target ?? null}
        onChange={(t) => {
          const label = targets.data?.find((x) => x.name === t)?.label;
          onChange({ target: t, version: PRODUCTION, agentName: label ?? "" });
        }}
        waiting={value.group ? "Loading…" : "Pick a project first"}
      />
      <VersionColumn connectionId={connection.id} value={value} onChange={onChange} />
    </div>
  );
}
